import { catalogValueLabel } from './catalog-labels.ts'

export const VALUE_FORMATS = ['number', 'integer', 'decimal', 'currency', 'percent', 'duration', 'date', 'datetime', 'text'] as const
export type ValueFormat = typeof VALUE_FORMATS[number]

export interface FormatOptions {
  format?: ValueFormat
  currency?: string
  locale?: string
  compact?: boolean
  /** Percent values are ratios (0.25 = 25 %) unless percentIsWhole is set */
  percentIsWhole?: boolean
  /** Host translations of catalog keys (paymentLink → Payment link). */
  labels?: Record<string, string> | null
}

const CURRENCY_RE = /^[A-Z]{3}$/

/** Johku locales are fi_FI style; Intl wants fi-FI. */
export function toBcp47 (locale?: string | null): string {
  if (!locale || typeof locale !== 'string') {
    return 'en-US'
  }
  const tag = locale.replace('_', '-')
  try {
    return Intl.getCanonicalLocales(tag)[0] ?? 'en-US'
  } catch {
    return 'en-US'
  }
}

export function formatValue (value: unknown, options: FormatOptions = {}): string {
  if (value === null || value === undefined || value === '') {
    return '–'
  }
  const locale = toBcp47(options.locale)
  const format = options.format ?? (typeof value === 'number' ? 'number' : 'text')

  if (format === 'text') {
    return catalogValueLabel(value, options.labels) ?? String(value)
  }
  if (format === 'date' || format === 'datetime') {
    const date = typeof value === 'number' ? new Date(value < 1e12 ? value * 1000 : value) : new Date(String(value))
    if (Number.isNaN(date.getTime())) {
      return String(value)
    }
    return new Intl.DateTimeFormat(locale, format === 'date'
      ? { dateStyle: 'medium' }
      : { dateStyle: 'medium', timeStyle: 'short' }).format(date)
  }

  const num = typeof value === 'number' ? value : Number(value)
  if (!Number.isFinite(num)) {
    return String(value)
  }
  const compact = options.compact ? { notation: 'compact' as const, maximumFractionDigits: 1 } : {}

  switch (format) {
    case 'integer':
      return new Intl.NumberFormat(locale, { maximumFractionDigits: 0, ...compact }).format(num)
    case 'decimal':
      return new Intl.NumberFormat(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2, ...compact }).format(num)
    case 'currency': {
      const currency = options.currency && CURRENCY_RE.test(options.currency) ? options.currency : 'EUR'
      return new Intl.NumberFormat(locale, { style: 'currency', currency, ...compact }).format(num)
    }
    case 'percent':
      return new Intl.NumberFormat(locale, { style: 'percent', maximumFractionDigits: 1 }).format(options.percentIsWhole ? num / 100 : num)
    case 'duration':
      return formatDuration(num, locale)
    default:
      return new Intl.NumberFormat(locale, { maximumFractionDigits: 2, ...compact }).format(num)
  }
}

/** Seconds to a short human duration such as 2 h 5 min. */
export function formatDuration (seconds: number, locale = 'en-US'): string {
  const sign = seconds < 0 ? '-' : ''
  let rest = Math.abs(Math.round(seconds))
  const d = Math.floor(rest / 86400); rest -= d * 86400
  const h = Math.floor(rest / 3600); rest -= h * 3600
  const m = Math.floor(rest / 60)
  const nf = new Intl.NumberFormat(locale)
  const parts: string[] = []
  if (d) parts.push(nf.format(d) + ' d')
  if (h) parts.push(nf.format(h) + ' h')
  if (m || !parts.length) parts.push(nf.format(m) + ' min')
  return sign + parts.slice(0, 2).join(' ')
}

export function formatDelta (value: number, options: FormatOptions = {}): string {
  const formatted = formatValue(Math.abs(value), options)
  if (value > 0) return '+' + formatted
  if (value < 0) return '−' + formatted
  return formatted
}
